import React from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../redux";
import { setShowModal } from "../../../redux/reducers/TrialLimitation";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Btn } from "../../../shared/Btn";
import { sendGAEvent } from "../../../utils/utils";
import { getTrialItemLabels } from "../../../utils/trialItems";

type Props = {};

export const TrialLimitModal: React.FC<Props> = () => {
  const history = useHistory();
  const location = useLocation();
  const { showModal, limit, totalTransactions } = useSelector(
    (state: ReduxState) => state.TrialLimitation
  );
  const dispatch = useDispatch();
  const toggle = () => dispatch(setShowModal(!showModal));

  const { job } = useSelector((state: ReduxState) => state.JobData);

  const { items } = getTrialItemLabels(job, location);
  const total = totalTransactions;

  const handleUpgrade = () => {
    sendGAEvent({
      action: "paywall_upgrade_click",
      category: "Paywall",
      props: { item_type: items, variant: "ap_framed" },
    });
    dispatch(setShowModal(false));
    const path = location.pathname + location.search;
    const currentUrl = encodeURIComponent(
      path.startsWith("/app") ? path : `/app${path}`
    );
    history.push(`/account/upgrade?next=${currentUrl}`);
  };

  React.useEffect(() => {
    if (showModal) {
      sendGAEvent({
        action: "paywall_shown",
        category: "Paywall",
        props: { item_type: items, trigger: "trial_limit" },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const benefits =
    items === "transactions"
      ? [
          "Download all transactions from all statements",
          "Export to Excel, CSV, QuickBooks, Xero, and more",
          "Reconciliation and categorization included",
        ]
      : items === "invoices/receipts"
      ? [
          "Export every invoice to Excel or CSV",
          "Push approved bills straight into QuickBooks Online or Xero — no manual entry",
          "Batch-process and keep all your extracted invoices",
        ]
      : [
          "Download all extracted data",
          "Export to Excel, CSV, and accounting formats",
          "Batch processing across multiple documents",
        ];

  return (
    <Modal onHide={toggle} show={showModal} centered>
      <Modal.Header closeButton className="border-0 pb-0" />
      <Modal.Body className="text-center px-4 pb-4">
        <div className="mb-3" style={{ fontSize: "2.5rem", color: "#28a745" }}>
          <FontAwesomeIcon icon="check-circle" />
        </div>
        <h4 className="font-weight-bold mb-2">
          {total
            ? `${total} ${items} extracted`
            : "Sample saved to your Downloads folder"}
        </h4>
        <p className="mb-3" style={{ fontSize: "0.95rem", color: "#28a745" }}>
          {total ? (
            <>
              Sample file with the first {limit ?? 10} {items} saved to your
              Downloads folder.
            </>
          ) : (
            <>
              Preview file with the first {limit ?? 10} {items} is ready.
            </>
          )}
        </p>
        <p className="text-muted mb-4" style={{ fontSize: "0.9rem" }}>
          {total && limit && total > limit ? (
            <>
              The sample shows the exact format you'll receive. Upgrade when
              you're ready to download all{" "}
              <strong className="text-dark">{total}</strong> {items}.
            </>
          ) : items === "invoices/receipts" ? (
            <>
              Upgrade to export all your invoices or push them to
              QuickBooks/Xero.
            </>
          ) : (
            <>
              Upgrade when you're ready to download everything you've
              extracted.
            </>
          )}
        </p>

        <div
          className="text-left mb-4 px-2"
          style={{ background: "#f8f9fa", borderRadius: 8, padding: "12px 16px" }}
        >
          {benefits.map((benefit, i) => (
            <div key={i} className={i < benefits.length - 1 ? "mb-2" : ""}>
              <FontAwesomeIcon
                icon="check"
                className="text-success mr-2"
                style={{ fontSize: "0.85rem" }}
              />
              <span style={{ fontSize: "0.9rem" }}>{benefit}</span>
            </div>
          ))}
        </div>

        <Btn
          variant="primary"
          size="lg"
          className="w-100 mb-2"
          onClick={handleUpgrade}
        >
          {items === "invoices/receipts"
            ? "Unlock all invoices"
            : items === "transactions" && total
            ? `Unlock all ${total} transactions`
            : "Unlock full download"}
        </Btn>
        <Btn
          variant="link"
          size="sm"
          className="w-100 mb-2"
          onClick={handleUpgrade}
        >
          See all plans
        </Btn>
        <Btn
          variant="link"
          size="sm"
          className="w-100 mb-2 text-muted"
          onClick={toggle}
        >
          Maybe later
        </Btn>
        <div className="text-muted" style={{ fontSize: "0.78rem" }}>
          Plans start at $29/month. Cancel anytime, no long-term commitment.
        </div>
      </Modal.Body>
    </Modal>
  );
};
